import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'operations', label: 'Operations' },
  { value: 'billing', label: 'Billing' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'account_manager', label: 'Account Manager' },
];

const ROLE_COLORS = {
  super_admin: 'badge-red',
  operations: 'badge-blue',
  billing: 'badge-green',
  marketing: 'badge-orange',
  account_manager: 'badge-gray',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [testAccounts, setTestAccounts] = useState(null);
  const [creatingTest, setCreatingTest] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const loadStaff = () => {
    api.get('/employee/staff')
      .then(res => setStaff(res.data.staff))
      .catch(() => toast.error('Could not load staff'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStaff(); }, []);

  const onSubmit = async (data) => {
    setCreating(true);
    try {
      await api.post('/employee/staff', data);
      toast.success(`${data.first_name} ${data.last_name} added successfully!`);
      reset();
      setShowForm(false);
      loadStaff();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create staff member');
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/employee/staff/${id}`, { status: newStatus });
      toast.success(`Staff member ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadStaff();
    } catch {
      toast.error('Could not update status');
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="flex-between" style={{ marginBottom: 20 }}>
            <div>
              <div className="page-title">Staff Management</div>
              <div className="page-sub" style={{ margin: 0 }}>Manage employee accounts and roles.</div>
            </div>
            <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); reset(); }}>
              {showForm ? 'Cancel' : '+ Add staff member'}
            </button>
          </div>

          {showForm && (
            <div className="card" style={{ marginBottom: 20, maxWidth: 600 }}>
              <div className="card-header"><div className="card-title">Add new staff member</div></div>
              <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="two-col">
                    <div className="form-group">
                      <label className="form-label">First name</label>
                      <input className="form-input" {...register('first_name', { required: true })} />
                      {errors.first_name && <div className="form-error">Required</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last name</label>
                      <input className="form-input" {...register('last_name', { required: true })} />
                      {errors.last_name && <div className="form-error">Required</div>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Work email</label>
                    <input type="email" className="form-input" placeholder="name@affordabletelecom.com"
                      {...register('email', { required: true })} />
                    {errors.email && <div className="form-error">Required</div>}
                  </div>
                  <div className="two-col">
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input className="form-input" placeholder="(555) 000-0000" {...register('phone')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <select className="form-input" {...register('role', { required: true })}>
                        <option value="">Select role</option>
                        {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                      {errors.role && <div className="form-error">Required</div>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Temporary password</label>
                    <input type="password" className="form-input" placeholder="Min 8 characters"
                      {...register('password', { required: true, minLength: 8 })} />
                    {errors.password && <div className="form-error">Min 8 characters</div>}
                    <div className="form-error" style={{ color: 'var(--text-sub)' }}>
                      The staff member should change this on first login.
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={creating}>
                    {creating ? 'Creating...' : 'Create Staff Account'}
                  </button>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Status</th><th>Joined</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {staff.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.first_name} {s.last_name}</td>
                      <td style={{ fontSize: 12 }}>{s.email}</td>
                      <td style={{ fontSize: 12 }}>{s.phone || '—'}</td>
                      <td>
                        <span className={`badge ${ROLE_COLORS[s.role] || 'badge-gray'}`}>
                          {ROLE_OPTIONS.find(r => r.value === s.role)?.label || s.role}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${s.status === 'active' ? 'green' : 'red'}`}>{s.status}</span>
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDate(s.created_at)}</td>
                      <td>
                        <button className="btn btn-outline btn-sm"
                          onClick={() => toggleStatus(s.id, s.status)}
                          style={{ color: s.status === 'active' ? 'var(--red)' : 'var(--green)' }}>
                          {s.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Focus Group Test Accounts */}
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-header">
              <div className="card-title">Focus Group Test Accounts</div>
              <button className="btn btn-primary btn-sm" disabled={creatingTest} onClick={async () => {
                if (!window.confirm('This will create 5 test tenants and 1 test landlord at 147 Riverside Drive. Continue?')) return;
                setCreatingTest(true);
                try {
                  const res = await api.post('/employee/create-test-accounts');
                  setTestAccounts(res.data);
                  toast.success(res.data.message);
                } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                finally { setCreatingTest(false); }
              }}>{creatingTest ? 'Creating...' : 'Create Focus Group Accounts'}</button>
            </div>
            {testAccounts && (
              <div className="card-body">
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#16a34a' }}>
                  {testAccounts.message} — Property: {testAccounts.property}
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Role</th><th>Name</th><th>Email</th><th>Password</th><th>Unit</th><th>Lease Type</th></tr>
                    </thead>
                    <tbody>
                      {testAccounts.accounts.map((a, i) => (
                        <tr key={i}>
                          <td><span className={`badge badge-${a.role === 'Landlord' ? 'blue' : 'green'}`}>{a.role}</span></td>
                          <td style={{ fontWeight: 600 }}>{a.name}</td>
                          <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{a.email}</td>
                          <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{a.password}</td>
                          <td>{a.unit || '—'}</td>
                          <td style={{ fontSize: 12 }}>{a.lease || '—'} {a.note ? <span className="badge badge-gray">{a.note}</span> : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-outline btn-sm" style={{ marginTop: 12 }} onClick={() => {
                  const text = testAccounts.accounts.map(a => `${a.role}\t${a.name}\t${a.email}\t${a.password}\t${a.unit || ''}\t${a.lease || ''}`).join('\n');
                  navigator.clipboard.writeText('Role\tName\tEmail\tPassword\tUnit\tLease Type\n' + text);
                  toast.success('Copied to clipboard');
                }}>Copy All to Clipboard</button>
              </div>
            )}
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
